//this is counter widget
import React, { useEffect, useState } from 'react';
import DeviceIcons from '../deviceList/DeviceIcons';
import Modal from '../components/Modal';
import ToggleSwitch from '../components/ToggleSwitch';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import DropdownSimple from '../components/DropdownSimple';
import LastEventInfo from './LastEventInfo';
import WaterTariffCost from '../features/Devices/counter/WaterTariffCost';
import Chartjs from '../components/Chart/Chart';
import { getEventAggregate } from '../api/api';
import useEventsControl, { PERIOD } from '../components/Chart/useEventsControl';
import { getMonths, getYears } from '../components/Chart/utils';
import dayjs from 'dayjs';
import { useEventsCache } from '../features/Devices/counter/useEventsCache';
import ControlIcon from '../features/Devices/ControlIcon';

const TabIndices = {
  ALL_TIME: 0,
  MONTHLY: 1,
  YEARLY: 2,
};

const Counter = ({ device, timeZone }) => {
  const initialCons = (Math.round((device?.last_event?.value || 0) * 100) / 100).toFixed(2);
  const initialMonth = `${dayjs().year()}-${dayjs().month()}`;
  const initialYear = dayjs().year();
  const [cons, setCons] = useState(initialCons);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [months, setMonths] = useState([]);
  const [years, setYears] = useState([]);
  const { year, setYear } = useEventsControl();
  const [from, setFrom] = useState(null);
  const [month, setMonth] = useState(initialMonth);
  const { monthsCache, yearsCache, setMonthsCache, setYearsCache } = useEventsCache();

  const handleContainerClick = async (event) => {
    if (!event.target.closest('#dropdown-simple') && !event.target.closest('#icon-chart') && !event.target.closest('#modal-chart')) {
      setActiveIndex((prevIndex) => (prevIndex === Object.keys(TabIndices).length - 1 ? 0 : prevIndex + 1));
    }
  };

  const fetchEvents = async (period) => {
    if (period === PERIOD.MONTH && monthsCache[device.id]) {
      setMonths(monthsCache[device.id]);
      setCons(monthsCache[device.id][0]?.consumption);
      setFrom(monthsCache[device.id][0]?.date);
      return;
    }

    if (period === PERIOD.YEAR && yearsCache[device.id]) {
      setYears(yearsCache[device.id]);
      setCons(yearsCache[device.id][0]?.consumption);
      setFrom(yearsCache[device.id][0]?.date);
      return;
    }

    const events = await getEventAggregate({ device_id: device.id, period });
    if (!events?.length) return;

    if (period === PERIOD.MONTH) {
      const monthsData = getMonths(events);
      setMonths(monthsData);
      setMonthsCache(device.id, monthsData);
      setCons(monthsData[0].consumption);
      setFrom(monthsData[0].date);
    } else if (period === PERIOD.YEAR) {
      const yearsData = getYears(events);
      setYears(yearsData);
      setYearsCache(device.id, yearsData);
      setCons(yearsData[0].consumption);
      setFrom(yearsData[0].date);
    }
  };

  const handleResetValues = () => {
    setYear(initialYear);
    setMonth(initialMonth);
  };

  useEffect(() => {
    handleResetValues();
    const fetch = async () => {
      if (activeIndex === TabIndices.MONTHLY) {
        await fetchEvents(PERIOD.MONTH);
      } else if (activeIndex === TabIndices.YEARLY) {
        await fetchEvents(PERIOD.YEAR);
      } else if (activeIndex === TabIndices.ALL_TIME) {
        setCons(initialCons);
      }
    };

    fetch();
  }, [activeIndex]);

  const handleChange = (val) => {
    let selected;
    if (activeIndex === TabIndices.MONTHLY) {
      setMonth(val);
      selected = months.find((m) => m.val === val);
    } else {
      setYear(+val);
      selected = years.find((y) => y.val === +val);
    }
    if (selected) {
      setCons(selected.consumption);
      setFrom(selected.date);
    }
  };

  return (
    <div>
      <div
        onClick={handleContainerClick}
        className="bg-white first:rounded-tl-3xl cursor-pointer rounded-3xl last:rounded-br-3xl p-5 border-l select-none flex flex-col justify-between h-full tap-highlight-transparent">
        <div className="flex gap-x-4 items-center">
          <DeviceIcons device={device} />

          <div className={'overflow-x-hidden'}>
            <p className="truncate">
              {device?.type?.name} {device?.is_archived && ' (Архив)'}
            </p>
            <p className="truncate text-sm text-gray-400">{device?.serial_number}</p>
          </div>
        </div>

        <div className="mt-2 flex-1">
          <div className="truncate text-xs text-gray-400">
            <LastEventInfo device={device}></LastEventInfo>
          </div>
        </div>

        {!isNaN(new Date(device?.last_event?.at)) && (
          <>
            <div className="flex">
              <div className="flex-1">
                {activeIndex === TabIndices.ALL_TIME && <p className="h-6 mt-2">Показания</p>}

                {activeIndex === TabIndices.MONTHLY && (
                  <DropdownSimple
                    disabled={!months?.length}
                    options={months}
                    value={month}
                    onChange={handleChange}
                    id="dropdown-simple"
                    valkey="val"
                    optkey="opt"
                  />
                )}

                {activeIndex === TabIndices.YEARLY && (
                  <DropdownSimple
                    disabled={!years?.length}
                    options={years}
                    value={year}
                    onChange={handleChange}
                    id="dropdown-simple"
                    valkey="val"
                    optkey="opt"
                  />
                )}

                <p className="text-gray-800 mr-3 text-xl sm:text-xl md:text-xl lg:text-xl xl:text-2xl 2xl:text-2xl">
                  {cons !== undefined ? (
                    cons + ' m³'
                  ) : (
                    <div
                      className="inline-block h-5 w-5 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                      role="status"></div>
                  )}
                </p>
                {activeIndex === TabIndices.MONTHLY && device?.tariffs && <WaterTariffCost device_id={device.id} from={from} />}
              </div>
              <ControlIcon Icon={QueryStatsIcon} handler={() => setShowModal(true)} />
            </div>
            <div className="toggle-switch-container pointer-events-none">
              <ToggleSwitch id="toggle-switch" activeIndex={activeIndex} setActiveIndex={setActiveIndex}></ToggleSwitch>
            </div>
          </>
        )}

        <div id="modal-chart">
          <Modal classWidth={'relative w-11/12 xl:w-1/2 my-6 mx-3'} showModal={showModal} setShowModal={setShowModal} title="История данных устройства">
            <Chartjs device={device} timeZone={timeZone} />
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default Counter;
